import Yup from '../yup';
import findIndex from 'lodash/findIndex';
import {constants} from '@neosvoc/nv-platform-core-common';
import {range} from 'lodash';

const TEMPLATES = constants.EDITOR_TEMPLATES;
const OPERATORS = constants.EDITOR_OPERATORS;
const VARIABLES = constants.EDITOR_VARIABLES;

const invalidConditionValue = (value) =>({key: 'invalidConditionValue', params: value});
const invalidConditionOperator = (value) =>({key: 'invalidConditionOperator', params: value});
const invalidConditionQuestion = (value) =>({key: 'invalidConditionQuestion', params: value});
const invalidQuestion = (value) =>({key: 'invalidQuestion', params: value});
const invalidRow = (value) => ({key: 'invalidRow', params: value});
const invalidChoice = (value) => ({key: 'invalidChoice', params: value});
const invalidJumpTo = (value) =>({key: 'invalidJumpTo', params: value});

const logicValidatorSchemas = {
    [TEMPLATES.STRING_1]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.CONTAINS], invalidConditionOperator),
        value: Yup.string().required(),
    }),
    [TEMPLATES.TEXT_1]: (pageItem) => Yup.object().shape({}),
    [TEMPLATES.NUMBER_1]: (pageItem) => {
        let valueYupObj = Yup.number().required();
        if (pageItem.min && pageItem.min !== '') {
            valueYupObj = valueYupObj.min(pageItem.min, invalidConditionValue);
        }
        if (pageItem.max && pageItem.max !== '') {
            valueYupObj = valueYupObj.max(pageItem.max, invalidConditionValue);
        }
        return Yup.object().shape({
            operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
            value: valueYupObj,
        });
    },
    [TEMPLATES.NUMBER_2]: (pageItem) => {
        let valueYupObj = Yup.number().required();
        if (pageItem.min && pageItem.min !== '') {
            valueYupObj = valueYupObj.min(pageItem.min, invalidConditionValue);
        }
        if (pageItem.max && pageItem.max !== '') {
            valueYupObj = valueYupObj.test('is-value-valid', invalidConditionValue, function(value) {
                return value <= pageItem.max || (pageItem.exclusiveChoice && value === pageItem.exclusiveChoice.id);
            });
        }
        return Yup.object().shape({
            operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
            value: valueYupObj,
        });
    },
    [TEMPLATES.MULTIPLE_NUMBER_1]: (pageItem, currentItem) => {
        return Yup.object().shape({
            operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
            value: Yup.number().required().when('operator', (operator, schema) => {
                switch (operator) {
                    case OPERATORS.LT:
                        return schema.min(pageItem.min + 1, invalidConditionValue);
                    case OPERATORS.GT:
                        return schema.max(pageItem.max - 1, invalidConditionValue);
                    case OPERATORS.EQ:
                        return schema.min(pageItem.min, invalidConditionValue).max(pageItem.max, invalidConditionValue);
                    default:
                        return schema;
                }
            }),
            question: Yup.string().required().oneOf(pageItem.rows.map((choice) => choice.id), invalidConditionQuestion),
        });
    },
    [TEMPLATES.MULTIPLE_NUMBER_3]: (pageItem, currentItem) => {
        return Yup.object().shape({
            operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
            value: Yup.number().required().when('operator', (operator, schema) => {
                switch (operator) {
                    case OPERATORS.LT:
                        return schema.min(pageItem.min + 1, invalidConditionValue);
                    case OPERATORS.GT:
                        return schema.max(pageItem.max - 1, invalidConditionValue);
                    case OPERATORS.EQ:
                        return schema.min(pageItem.min, invalidConditionValue).max(pageItem.max, invalidConditionValue);
                    default:
                        return schema;
                }
            }),
            question: Yup.string().required().oneOf(pageItem.rows.map((choice) => choice.id), invalidConditionQuestion),
        });
    },
    [TEMPLATES.MULTIPLE_NUMBER_2]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
        value: Yup.number().required().min(pageItem.min, invalidConditionValue).max(pageItem.max, invalidConditionValue),
        question: Yup.string().required().oneOf(pageItem.rows.map((choice) => choice.id, invalidConditionQuestion)),
    }),
    [TEMPLATES.SINGLE_CHOICE_1]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.SINGLE_CHOICE_1_EXT]: (pageItem) => Yup.object().shape({}),
    [TEMPLATES.SINGLE_CHOICE_2]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.SINGLE_CHOICE_3]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.SINGLE_CHOICE_4]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.MULTIPLE_CHOICE_1]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.IN, OPERATORS.NOT_IN], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.MULTIPLE_CHOICE_2]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.IN, OPERATORS.NOT_IN], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.BATTERY_1]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
        question: Yup.mixed().oneOf(pageItem.rows.map((row) => row.id), invalidConditionQuestion),
    }),
    [TEMPLATES.BATTERY_2]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.IN, OPERATORS.NOT_IN], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
        question: Yup.mixed().oneOf(pageItem.rows.map((row) => row.id), invalidConditionQuestion),
    }),
    [TEMPLATES.DATE_1]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.GT, OPERATORS.LT, OPERATORS.EQ, OPERATORS.NEQ]),
        value: Yup.date().required(),
    }),
    [TEMPLATES.IMAGE_1]: (pageItem) => Yup.object().shape({}),
    [TEMPLATES.GEO_1]: (pageItem) => Yup.object().shape({}),
    [TEMPLATES.GENERATOR_CLASSIFIER]: (pageItem) => Yup.object().shape({ // Same as singleChoice
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [TEMPLATES.RANK_1]: (pageItem) => {
        const baseValueSchema = Yup.number()
            .transform((value, originalValue) => (originalValue === '' || originalValue === null ? undefined : value))
            .typeError(invalidConditionValue)
            .required(invalidConditionValue);

        return Yup.object().shape({
            operator: Yup.string().required().oneOf([OPERATORS.LT, OPERATORS.GT, OPERATORS.EQ], invalidConditionOperator),
            value: baseValueSchema.when('operator', (operator, schema) => {
                switch (operator) {
                    case OPERATORS.LT:
                        return schema.oneOf(range(2, pageItem.rows.length + 1), invalidConditionValue);
                    case OPERATORS.GT:
                        return schema.oneOf(range(1, pageItem.rows.length), invalidConditionValue);
                    case OPERATORS.EQ:
                        return schema.oneOf(range(1, pageItem.rows.length + 1), invalidConditionValue);
                    default:
                        return schema;
                }
            }),
            question: Yup.string().required().oneOf(pageItem.rows.map((choice) => choice.id), invalidConditionQuestion),
        });
    },
    [TEMPLATES.GENERATOR_CLASSIFIER_2]: (pageItem) => Yup.object().shape({ // Same as singleChoice
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.mixed().oneOf(pageItem.choices.map((choice) => choice.id), invalidConditionValue),
    }),
    [VARIABLES.PARAMETERS]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.string().required(),
    }),
    [VARIABLES.EXTRA_FIELDS]: (pageItem) => Yup.object().shape({
        operator: Yup.string().required().oneOf([OPERATORS.EQ, OPERATORS.NEQ], invalidConditionOperator),
        value: Yup.string().required(),
    }),
};

// to access context It cannot be an arrow function
function ConditionTestFn(item) {
    if (this.parent.id !== '') {
        const pageItem = this.options.context.pageItems.find((pageItem) => pageItem.id === this.parent.id);
        if (pageItem) {
            return logicValidatorSchemas[pageItem.type](pageItem, item).validate(item, {abortEarly: false}).catch((yupError) => {
                const errors = [];
                for (const err of yupError.inner) {
                    errors.push(this.createError({path: `${this.path}.${err.path}`, message: err.message}));
                }
                throw new Yup.ValidationError({inner: errors});
            });
        }
        const extraField = this.options.context.extraFields.find((field) => `extraFields.${field.name}` === this.parent.id);
        if (extraField) {
            return logicValidatorSchemas[VARIABLES.EXTRA_FIELDS](extraField, item).validate(item, {abortEarly: false}).catch((yupError) => {
                const errors = [];
                for (const err of yupError.inner) {
                    errors.push(this.createError({path: `${this.path}.${err.path}`, message: err.message}));
                }
                throw new Yup.ValidationError({inner: errors});
            });
        }
        const parameter = this.options.context.parameters.find((param) => `parameters.${param.name}` === this.parent.id);
        if (parameter) {
            return logicValidatorSchemas[VARIABLES.PARAMETERS](parameter, item).validate(item, {abortEarly: false}).catch((yupError) => {
                const errors = [];
                for (const err of yupError.inner) {
                    errors.push(this.createError({path: `${this.path}.${err.path}`, message: err.message}));
                }
                throw new Yup.ValidationError({inner: errors});
            });
        }
    }
}

const validExpressionIdFn = (id, items, pages, pageId, parameters, extraFields, strict = true) => {
    const parameter = parameters.find((item) => (`parameters.${item.name}` === id));
    if (parameter) {
        return true;
    }
    const extraField = extraFields.find((item) => (`extraFields.${item.name}` === id));
    if (extraField) {
        return true;
    }
    const pageIndex = findIndex(pages, (page) => (page.id === pageId));
    const item = items.find((item) => (item.id === id));
    if (item) {
        const idIndex = item.pageIndex;
        return strict? idIndex < pageIndex: idIndex <= pageIndex;
    } else {
        return false;
    }
};

const getValidExpressionIdFn = (pageId) => (
    // to access context It cannot be an arrow function
    function(id) {
        // eslint-disable-next-line no-invalid-this
        const {pageItems, pages, parameters, extraFields} = this.options.context;
        return validExpressionIdFn(id, pageItems, pages, pageId, parameters, extraFields, true);
    }
);

const getValidPostscriptExpressionIdFn = (pageId) => (
    // to access context It cannot be an arrow function
    function(id) {
        // eslint-disable-next-line no-invalid-this
        const {pageItems, pages, parameters, extraFields} = this.options.context;
        return validExpressionIdFn(id, pageItems, pages, pageId, parameters, extraFields, false);
    }
);

const expressionSchema = (expressionIdFn) => (
    Yup.object().shape({
        id: Yup.string().test('is-valid', invalidQuestion, expressionIdFn),
        logicOp: Yup.string().required(),
        condition: Yup.object().test('is-valid-condition', ConditionTestFn),
    }));

const getGroupSchema = (pageId) => (Yup.lazy((item) => {
    if (item.id !== undefined) {
        return expressionSchema(getValidExpressionIdFn(pageId));
    } else {
        return Yup.object().shape({
            logicOp: Yup.string().required(),
            conditions: Yup.array().of(getGroupSchema(pageId)).required(),
        });
    }
}));

const getPostscriptGroupSchema = (pageId) => (Yup.lazy((item) => {
    if (item.id !== undefined) {
        return expressionSchema(getValidPostscriptExpressionIdFn(pageId));
    } else {
        return Yup.object().shape({
            logicOp: Yup.string().required(),
            conditions: Yup.array().of(getPostscriptGroupSchema(pageId)).required(),
        });
    }
}));

const getValidRowIdFn = (pageId) => (
    // to access context It cannot be an arrow function
    function ValidRowFn(id) {
        // eslint-disable-next-line no-invalid-this
        const pages = this.options.context.pages;
        const rows = pages.find((page) => page.id === pageId).rows;
        return rows? rows.includes(id): false;
    }
);

const getValidChoiceIdFn = (pageId) => (
    // to access context It cannot be an arrow function
    function ValidChoiceFn(id) {
        // eslint-disable-next-line no-invalid-this
        const pages = this.options.context.pages;
        const choices = pages.find((page) => page.id === pageId).choices;
        return choices? choices.includes(id): false;
    }
);

const getValidJumpToFn = (pageId) => (
    // to access context It cannot be an arrow function
    function ValidJumpToFn(jumpTo) {
        // eslint-disable-next-line no-invalid-this
        const pages = this.options.context.pages;
        const pageIndex = findIndex(pages, (page) => (page.id === pageId));
        const jumpToIndex = findIndex(pages, (page) => (page.id === jumpTo));
        return jumpToIndex > pageIndex;
    }
);

export const getPreScriptValidatorSchema = (pageId) => (
    Yup.object().shape({
        expression: Yup.array().of(getGroupSchema(pageId)),
        jumpTo: Yup.string().required().test('is-valid-jumpTo', invalidJumpTo, getValidJumpToFn(pageId)),
    })
);

export const getPostScriptValidatorSchema = (pageId) => (
    Yup.object().shape({
        expression: Yup.array().of(getPostscriptGroupSchema(pageId)),
        jumpTo: Yup.string().required().test('is-valid-jumpTo', invalidJumpTo, getValidJumpToFn(pageId)),
    })
);

export const getOptionsScriptValidatorSchema = (pageId) => (
    Yup.object().shape({
        expression: Yup.array().of(getGroupSchema(pageId)),
        id: Yup.string().required().test('is-valid-optionid', invalidChoice, getValidChoiceIdFn(pageId)),
    })
);

export const getRowsScriptValidatorSchema = (pageId) => (
    Yup.object().shape({
        expression: Yup.array().of(getGroupSchema(pageId)),
        id: Yup.string().required().test('is-valid-rowid', invalidRow, getValidRowIdFn(pageId)),
    })
);

export const getChoiceScriptValidatorSchema = (pageId) => {
    return Yup.object().shape({
        expression: Yup.array().of(getGroupSchema(pageId)),
    });
};

const validAlertExpressionIdFn = (id, items, parameters, extraFields) => {
    const parameter = parameters.find((item) => (`parameters.${item.name}` === id));
    if (parameter) {
        return true;
    }
    const extraField = extraFields.find((item) => (`extraFields.${item.name}` === id));
    if (extraField) {
        return true;
    }
    return !!items.find((item) => (item.id === id));
};

const getValidAlertExpressionIdFn = () => (
    // to access context It cannot be an arrow function
    function(id) {
        // eslint-disable-next-line no-invalid-this
        const {pageItems, parameters, extraFields} = this.options.context;
        return validAlertExpressionIdFn(id, pageItems, parameters, extraFields);
    }
);

const getAlertGroupSchema = () => (Yup.lazy((item) => {
    if (item.id !== undefined) {
        return expressionSchema(getValidAlertExpressionIdFn());
    } else {
        return Yup.object().shape({
            logicOp: Yup.string().required(),
            conditions: Yup.array().of(getAlertGroupSchema()).required(),
        });
    }
}));

export const getAlertFilterValidatorSchema = () => (
    Yup.array().of(getAlertGroupSchema())
);

export default logicValidatorSchemas;
